"use client";

import {Radio as RACRadio, RadioProps} from "react-aria-components";
import clsMerge from "../../util/clsMerge";
import {ReactNode, ForwardedRef, forwardRef} from "react";

export enum RadioVariant {
	DEFAULT = "default",
	WHITE = "white",
}

interface RadioStyle {
	default: String;
	selected?: String;
	disabled?: String;
	invalid?: String;
	focused?: String;
}

export const radioStylesByVariant: Record<RadioVariant, RadioStyle> = {
	[RadioVariant.DEFAULT]: {
		default: "",
	},
	[RadioVariant.WHITE]: {
		default:
			"rounded border-1 py-2 pl-3 pr-4 hover:bg-gray-100 border-gray-500 hover:border-gray-300 cursor-pointer",
		selected:
			"bg-pale-light-cyan hover:bg-pale-light-cyan border-hyperlink hover:border-hyperlink transition",
		focused: "border-hyperlink hover:border-hyperlink",
		disabled:
			"bg-gray-50 border-gray-500 hover:border-gray-500 hover:bg-gray-50 text-gray-600 cursor-not-allowed",
	},
};

interface RadioPropsWithStyle
	extends RadioProps,
		React.RefAttributes<HTMLLabelElement> {
	variant?: RadioVariant;
	selectedStyles?: String;
	disabledStyles?: String;
	invalidStyles?: String;
	focusedStyles?: String;

	//TODO: make child also a function potentially
	children: ReactNode;
}

// https://react-spectrum.adobe.com/react-aria/RadioGroup.html
const Radio = (
	{
		children,
		className,
		variant = RadioVariant.DEFAULT,
		selectedStyles = "",
		disabledStyles = "",
		invalidStyles = "",
		focusedStyles = "",
		...props
	}: RadioPropsWithStyle,
	ref: ForwardedRef<HTMLLabelElement>
) => {
	const classString = clsMerge(
		"rounded-full text-md",
		radioStylesByVariant[variant].default,
		className
	);
	return (
		<RACRadio
			className={clsMerge("flex flex-row gap-2", className)}
			ref={ref}
			{...props}
		>
			{({isSelected, isDisabled, isInvalid, isFocused}) => {
				const classStringWithProperties = clsMerge(
					classString,
					isSelected &&
						clsMerge(radioStylesByVariant[variant]?.selected, selectedStyles),
					isDisabled &&
						clsMerge(radioStylesByVariant[variant]?.disabled, disabledStyles),
					isInvalid &&
						clsMerge(radioStylesByVariant[variant]?.invalid, invalidStyles),
					isFocused &&
						clsMerge(radioStylesByVariant[variant]?.focused, focusedStyles)
				);
				return (
					<div
						className={clsMerge(
							"flex flex-row gap-2",
							classStringWithProperties
						)}
					>
						<div
							className={clsMerge(
								"checkbox h-5 pt-1 text-gray-800",
								isDisabled && "text-gray-600",
								isInvalid && "text-rich-orange"
							)}
						>
							<svg
								className={clsMerge(
									"h-full rounded-full",
									isFocused && "ring ring-navy-200"
								)}
								viewBox="0 0 18 18"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<circle
									cx="9"
									cy="9"
									r="8"
									fill={isDisabled ? "#e5e3dc" : "white"}
									stroke="currentColor"
								/>
								{isSelected && (
									<circle cx="9" cy="9" r="4" fill="currentColor" />
								)}
							</svg>
						</div>
						<>{children}</>
					</div>
				);
			}}
		</RACRadio>
	);
};

export default forwardRef<HTMLLabelElement, RadioPropsWithStyle>(Radio);
